import React from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { format } from "date-fns";
import type {
  ShipmentEventApi,
  DownloadAttachmentRequest,
  DownloadAttachmentResponse,
} from "../../../src/shipments/types";
import { apiClient } from "../utils/apiClient";
import { EventLogger } from "@/utils/eventLogger";

interface ShipmentTimelineMessageBubbleProps {
  event: ShipmentEventApi;
  showSubject: boolean; // false if same thread as previous message
  showSender: boolean; // false if same sender as previous message
  showDateBullet: boolean; // true if this is a new day
  onOpenEmail: (emailId: string) => void;
}

// Layout constants
const BUBBLE_WIDTH_PERCENT = 60;

// Colors for iOS-style bubbles
const OWN_DOMAIN_BG = "#E3F2FD"; // Light blue
const OTHER_DOMAIN_BG = "#F5F5F5"; // Light gray

/**
 * Decode HTML entities in text
 */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Extract email from text like "Melike Kozan <melike@ajww.com>"
 */
function extractEmail(text: string): string {
  const match = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? match[0] : text;
}

/**
 * iOS-style message bubble for shipment timeline
 * Displays emails as chat bubbles with subject, sender, snippet, and attachments
 */
export function ShipmentTimelineMessageBubble({
  event,
  showSubject,
  showSender,
  showDateBullet,
  onOpenEmail,
}: ShipmentTimelineMessageBubbleProps) {
  const isOwnDomain = event.fromOwnDomain ?? false;
  const eventDate = event.date ? new Date(event.date) : null;

  const handleClick = () => {
    if (event.emailId) {
      onOpenEmail(event.emailId);
    }
  };

  const handleDownloadAttachment = async (
    emailId: string,
    attachmentId: string,
    filename?: string
  ) => {
    try {
      const response = await apiClient.post<
        DownloadAttachmentResponse,
        DownloadAttachmentRequest
      >("/shipments/download-attachment", { emailId, attachmentId });

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = response.downloadUrl;
      link.download = response.filename || filename || "attachment";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      EventLogger.getInstance().track({
        name: "click",
        action: "download_attachment",
        attachmentName: response.filename || filename || "<unknown>",
      });
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const senderEmail = event.emailFrom?.[0]
    ? extractEmail(event.emailFrom[0])
    : "Unknown sender";
  const displayTime = eventDate ? format(eventDate, "h:mma, MMM d") : "";

  // Decode HTML entities in snippet and truncate to 80 chars
  const rawSnippet = event.snippet
    ? decodeHtmlEntities(event.snippet)
    : event.name || "No content";
  const snippetText =
    rawSnippet.length > 80 ? `${rawSnippet.slice(0, 80)}...` : rawSnippet;

  return (
    <Box
      sx={{
        position: "relative",
        pl: "66px", // Space for timeline + 6px gap
        mb: 2,
      }}
    >
      {/* Date bullet on the left - only show for new days */}
      {showDateBullet && eventDate && (
        <>
          {/* Date text */}
          <Tooltip
            title={format(eventDate, "MMM d, yyyy 'at' h:mm a")}
            placement="left"
          >
            <Typography
              variant="caption"
              color="primary.main"
              sx={{
                position: "absolute",
                left: -10,
                top: 6,
                fontSize: "0.65rem",
                fontWeight: 500,
                textAlign: "right",
                width: 50,
              }}
            >
              {format(eventDate, "MMM d")}
            </Typography>
          </Tooltip>

          {/* Timeline circle */}
          <Box
            sx={{
              position: "absolute",
              left: 44,
              top: 4,
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: "primary.main",
              zIndex: 1,
              border: "3px solid white",
              boxShadow: 1,
            }}
          />
        </>
      )}

      {/* Content area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isOwnDomain ? "flex-start" : "flex-end",
        }}
      >
        {/* Subject - always aligned left, only if showSubject is true */}
        {showSubject && event.emailSubject && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "primary.main",
              mt: "6px", // Align with timeline bullets
              mb: 0.25,
              width: "100%",
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {decodeHtmlEntities(event.emailSubject)}
          </Typography>
        )}

        {/* Sender email - only if showSender is true */}
        {showSender && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "11px",
              color: "text.secondary",
              mb: 0.5,
              maxWidth: `${BUBBLE_WIDTH_PERCENT}%`,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              alignSelf: isOwnDomain ? "flex-start" : "flex-end",
            }}
          >
            {senderEmail}
          </Typography>
        )}

        {/* Message bubble */}
        <Box
          onClick={handleClick}
          sx={{
            backgroundColor: isOwnDomain ? OWN_DOMAIN_BG : OTHER_DOMAIN_BG,
            borderRadius: "12px",
            padding: 1.5,
            maxWidth: `${BUBBLE_WIDTH_PERCENT}%`,
            minWidth: "150px",
            cursor: event.emailId ? "pointer" : "default",
            transition: "all 0.15s ease",
            "&:hover": event.emailId
              ? {
                  backgroundColor: isOwnDomain ? "#BBDEFB" : "#EEEEEE",
                  transform: "scale(1.01)",
                }
              : {},
          }}
        >
          {/* Snippet content */}
          <Typography
            variant="body2"
            sx={{
              fontSize: "13px",
              color: "text.primary",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {snippetText}
          </Typography>

          {/* Time */}
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              color: "text.disabled",
              display: "block",
              mt: 1,
              textAlign: isOwnDomain ? "left" : "right",
            }}
          >
            {displayTime}
          </Typography>
        </Box>

        {/* Attachment chips */}
        {event.attachments && event.attachments.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 1,
              maxWidth: `${BUBBLE_WIDTH_PERCENT}%`,
              justifyContent: isOwnDomain ? "flex-start" : "flex-end",
            }}
          >
            {event.attachments.map((att) => (
              <Chip
                key={att.emailAttachmentId}
                label={att.filename || "Attachment"}
                size="small"
                icon={<AttachFileIcon sx={{ fontSize: "14px !important" }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (event.emailId) {
                    handleDownloadAttachment(
                      event.emailId,
                      att.emailAttachmentId,
                      att.filename
                    );
                  }
                }}
                sx={{
                  height: "22px",
                  fontSize: "10px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  "& .MuiChip-label": {
                    px: 0.75,
                  },
                  "& .MuiChip-icon": {
                    ml: 0.5,
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
